import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trash2, Download, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { getLogs, clearLogs, getLogsAsText } from '../utils/logger';

const DebugLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadLogs = () => {
    setLogs(getLogs());
  };

  useEffect(() => {
    loadLogs();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadLogs, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const handleClear = () => {
    clearLogs();
    setLogs([]);
    toast.success('Logs cleared');
  };

  const handleCopy = () => {
    const text = getLogsAsText();
    navigator.clipboard.writeText(text);
    toast.success('Logs copied to clipboard');
  };

  const handleDownload = () => {
    const text = getLogsAsText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `privluxe-logs-${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Logs downloaded');
  };

  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(l => l.type === filter);

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return '#EF4444';
      case 'warn': return '#F59E0B';
      case 'info': return '#3B82F6';
      case 'debug': return '#8B5CF6';
      default: return '#9CA3AF';
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #1A1F3C 50%, #0D1225 100%)' }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3 border-b safe-area-top"
        style={{ 
          background: 'rgba(10, 15, 44, 0.95)',
          backdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">Debug Logs</h1>
              <p className="text-xs text-gray-400">{logs.length} entries</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={loadLogs}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              className="text-gray-400 hover:text-white"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              className="text-gray-400 hover:text-white"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Filter buttons */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {['all', 'log', 'error', 'warn', 'info', 'debug'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              {f.toUpperCase()}
              {f !== 'all' && (
                <span className="ml-1 opacity-60">
                  ({logs.filter(l => l.type === f).length})
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Auto-refresh toggle */}
        <div className="flex items-center gap-2 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-xs text-gray-400">Auto-refresh (2s)</span>
          </label>
        </div>
      </header>

      {/* Logs */}
      <main className="p-2">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No logs yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {[...filteredLogs].reverse().map((log, idx) => (
              <div 
                key={idx}
                className="p-2 rounded text-xs font-mono break-all"
                style={{ background: 'rgba(0,0,0,0.3)' }}
              >
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 shrink-0">{log.time}</span>
                  <span 
                    className="font-bold shrink-0"
                    style={{ color: getLogColor(log.type) }}
                  >
                    [{log.type.toUpperCase()}]
                  </span>
                  <span className="text-gray-300 whitespace-pre-wrap">{log.message}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DebugLogs;
